import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  EPurchaseCostReviewStatus,
  IHeadlineLineItemBreakdownAccommodationLineItem,
  ERateSource,
  BookingSearchItem,
  IHeadlineLineItemBreakdownFrontend,
  makeBookingManagerApi,
} from 'services/BookingManagerApi';
import { formatDate, formatPrice, getCurrency, getCurrencyBySymbol, getCurrencySymbol } from 'utils';
import { DecimalInput } from '../stateful/DecimalInput';
import styled from 'styled-components';
import { PurchaseCostReviewStatus } from 'ui/PurchaseCostReviewStatus';
import classnames from 'classnames';
import { AccommodationBlock } from './AccommodationBlock';
import { FinanceAdjustmentProductsBlock, ProductsBlock } from './ProductsBlock';
import { AdditionalChargesBlock } from './AdditionalChargesBlock';
import FluidButton from 'ui/FluidButton';
import { ENetworkRequestStatus } from 'services/BackendApi';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import * as BreakdownSelectors from 'store/modules/bookingManager/subdomains/breakdown/selectors';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { AddActivityModalContainer } from '../../containers/AddActivityModalContainer/AddActivityModalContainer';
import { AddTransferModalContainer } from 'containers/AddTransferModalContainer/AddTransferModalContainer';
import { AddGroundServiceModalContainer } from 'containers/AncillaryGroundServices/AddGroundServiceModalContainer';
import { GenericProductsBlock } from './GenericProductBlock';
import { EditAncillaryProductModalContainer } from 'containers/EditAncillaryProductModalContainer/EditAncillaryProductModalContainer';
import { isNil, sortBy } from 'lodash-es';
import { AnnouncementsDisplay } from 'ui/AnnouncementsDisplay/AnnouncementsDisplay';
import * as DashboardSelectors from 'store/modules/bookingManager/subdomains/dashboard/selectors';
import { AddAccommodationModal } from 'ui/AddAccommodationModal/AddAccommodationModal';
import { VerticalSpace } from 'ui/VerticalSpace';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import { RefreshComponent } from 'ui/Icons';
import { useAvailableCurrencies } from 'hooks/useAvailableCurrencies';
import { getCountryCodeForCurrency } from 'utils/currencyConversion';
import Flag from 'react-world-flags';
import {
  getExchangeCurrencyRequestAction,
  refreshExchangeCurrencyAction,
  resetExchangeCurrencyAction,
} from 'store/modules/bookingManager/subdomains/breakdown/actions';
import { TCurrencyCode } from 'interfaces';
import { HidingTooltip } from 'ui/Tooltip';
import { automaticProformaInvoiceToTaSelector } from 'store/modules/bookingManager/subdomains/finance/selectors';
import { EditAccommodationModal } from 'ui/EditAccommodationModal';
import { SubtotalsRow } from './SubtotalsRow';
import { useAllocationMatrixQuery } from 'containers/BookingManagerGuestAllocation/useAllocationMatrixQuery';
import {
  AllocationMatrixData,
  AllocationMatrixGuest,
  AllocationsItem,
} from 'services/BookingManagerApi/types/AllocationsResponse';

export interface IHeadlineLineItemBreakdownComponent {
  bookingUuid: string;
  className?: string;
  headlineLineItemBreakdown: IHeadlineLineItemBreakdownFrontend | null;
  bookingCurrency?: string | null;
  addHeadlineLineItemAccommodationAction: typeof BreakdownActions.addHeadlineLineItemAccommodationAction;
  setHeadlineLineItemFieldAction: typeof BreakdownActions.setHeadlineLineItemFieldAction;
  addHeadlineLineItemAction: typeof BreakdownActions.addHeadlineLineItemAction;
  removeHeadlineLineItemAction: typeof BreakdownActions.removeHeadlineLineItemAction;
  setHeadlineLineItemBreakdownNotesAction: typeof BreakdownActions.setHeadlineLineItemBreakdownNotesAction;
  setHeadlineLineItemBreakdownMarginPercentage: typeof BreakdownActions.setHeadlineLineItemBreakdownMarginPercentageAction;
  setBreakdownHold: (
    accommodationIndex: number,
    hold?: IHeadlineLineItemBreakdownAccommodationLineItem['hold']
  ) => void;
  bookingStatus: string;
  purchaseCostReviewStatus?: EPurchaseCostReviewStatus | null;
  isEditable: boolean;
  isMarginEditable: boolean;
  isPurchaseCostEditable: boolean;
  isSR: boolean;
  isTA: boolean;
  isFinanceUser: boolean;
  isFinanceWithCompletedStatus: boolean;
  isLiveRate: boolean;
  isAdmin: boolean;
  isSales: boolean;
  isLatestVersion: boolean;
  headlineLineItemBreakdownLoad: ENetworkRequestStatus;
  resortConfirmationNumber?: string | null;
  bookingSearchItem: BookingSearchItem | null;
}

const MarginDisplaySpan = styled.span`
  display: block;
  border-left: 6px solid #d5d2d1;
  margin-left: 16px;
  padding-left: 16px;
  width: 200px;
  height: 42px;
`;

const CurrencyFlag = ({ currencyCode, className }: { currencyCode: string; className?: string }) => {
  const countryCode = getCountryCodeForCurrency(currencyCode);
  if (!countryCode) {
    return <span className={`inline-block w-[18px] text-center ${className || ''}`}>•</span>;
  }
  return <Flag code={countryCode} className={`w-[18px] h-[13px] inline-block ${className || ''}`} />;
};

const GA_FLOW_TAG = 'act-bkd';

const getAllocationItems = (
  items: Array<{ uuid: string; groupUuid?: string }>,
  guests: Record<string, AllocationMatrixGuest>,
  allocations: AllocationsItem[],
  grouped: boolean
): Record<string, AllocationMatrixGuest[]> => {
  const allocationItems: Record<string, AllocationMatrixGuest[]> = {};

  for (const item of items) {
    if (item.groupUuid && grouped) {
      const seenGroups = new Set<string>();
      if (!seenGroups.has(item.groupUuid)) {
        const allocatedGuests = allocations
          .filter(allocation => allocation.breakdownItemUuid === item.groupUuid)
          .map(allocation => guests[allocation.guestUuid]);
        allocationItems[item.groupUuid] = allocatedGuests;
        seenGroups.add(item.groupUuid);
      }
    } else {
      const allocatedGuests = allocations
        .filter(allocation => allocation.breakdownItemUuid === item.uuid)
        .map(allocation => guests[allocation.guestUuid]);
      allocationItems[item.uuid] = allocatedGuests;
    }
  }
  return allocationItems;
};

export const HeadlineLineItemBreakdown = (props: IHeadlineLineItemBreakdownComponent) => {
  if (!props.headlineLineItemBreakdown) {
    return null;
  }
  const dispatch = useDispatch();

  const transfersSuppliers = useSelector(BreakdownSelectors.breakdownTransfersSuppliersSelector);
  const groundServicesSuppliers = useSelector(BreakdownSelectors.breakdownGroundServicesSuppliersSelector);
  const activitiesSuppliers = useSelector(BreakdownSelectors.breakdownActivitiesSuppliersSelector);

  const topNavigationData = useSelector(DashboardSelectors.topNavigationDataDashboardSelector);

  const headlineLineItemBreakdown: IHeadlineLineItemBreakdownFrontend = props.headlineLineItemBreakdown;
  const shouldShowPurchaseCost = props.isSR || props.isFinanceUser;

  const accommodationsGroupedByHotel = Object.values(headlineLineItemBreakdown.Accommodation.items).reduce(
    (acc, accom) => {
      if (!acc[accom.hotelUuid]) {
        acc[accom.hotelUuid] = { hotelName: accom.hotelName, hotelUuid: accom.hotelUuid, rooms: [] };
      }
      acc[accom.hotelUuid].rooms.push(accom);
      return acc;
    },
    {}
  );
  const roomGroups = Object.values(accommodationsGroupedByHotel) as Array<{
    hotelName: string;
    hotelUuid: string;
    rooms: IHeadlineLineItemBreakdownAccommodationLineItem[];
  }>;

  // Since OWA-8928 shapes for breakdown changed, we need to check if the old version is used
  // to show the proper fields in past data.
  // Best approach is if we still have info in Fine
  // @ts-ignore
  const isOldVersion = headlineLineItemBreakdown.Fine.items.length > 0;

  const { dynamicParameters, dynamicParametersAreEmpty } = useDynamicParameters();

  const { getAllocationMatrix } = useAllocationMatrixQuery(makeBookingManagerApi(), props.bookingUuid);
  const allocationData: AllocationMatrixData | undefined = getAllocationMatrix?.data?.data?.data;
  const allocations = allocationData?.allocations || [];
  const allocationGuests = useMemo(() => {
    const guests = allocationData?.guests || [];
    const guestMap: Record<string, AllocationMatrixGuest> = {};
    guests.forEach(guest => {
      guestMap[guest.uuid] = guest;
    });
    return guestMap;
  }, [allocationData]);

  const accommodationsAllocations = useMemo(
    () =>
      getAllocationItems(
        Object.values(headlineLineItemBreakdown.Accommodation.items),
        allocationGuests,
        allocations,
        false
      ),
    [headlineLineItemBreakdown.Accommodation.items, allocationGuests, allocations]
  );

  const handleAddAncillaryActivity = useCallback(() => {
    if (dynamicParameters.GOOGLE_ANALYTICS_TRACK_ID) {
      // @ts-ignore
      window.gtag('event', `${GA_FLOW_TAG}_add-item`, {});
    }
    dispatch(BreakdownActions.openAddActivityModalAction());
  }, [dispatch]);

  const handleAddAncillaryTransfer = useCallback(() => {
    dispatch(BreakdownActions.setAddAncillaryTransferModalToggleAction(true));
  }, [dispatch]);

  const handleAddAncillaryGroundService = useCallback(() => {
    dispatch(BreakdownActions.setAddAncillaryGroundServiceModalToggleAction(true));
  }, [dispatch]);

  const handleAddAccommodationButton = useCallback(() => {
    if (dynamicParameters.ENABLE_ACCOMM_MODALS_TEMP) {
      dispatch(BreakdownActions.setAddAncillaryAccommodationModalToggleAction(true));
    } else {
      props.addHeadlineLineItemAccommodationAction();
    }
  }, [props.addHeadlineLineItemAccommodationAction]);

  const handleSelectExchangeCurrency = useCallback(
    sv => {
      const currency = sv[0];
      if (currency) {
        dispatch(getExchangeCurrencyRequestAction(sv[0] as TCurrencyCode));
      } else {
        dispatch(resetExchangeCurrencyAction());
      }
    },
    [dispatch]
  );

  const handleRefreshCurrencyChange = useCallback(() => dispatch(refreshExchangeCurrencyAction()), [dispatch]);

  const isPaymentCurrencyChangeEnabled = !!dynamicParameters.ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP;

  // Legacy options (flag OFF) — from EXCHANGE_CURRENCIES_ISO_CODES dynamic parameter
  const availableCurrenciesToExchange = useMemo(() => {
    const bookingCurrencyCode = getCurrencyBySymbol(props.bookingCurrency ?? '').code;
    return dynamicParameters.EXCHANGE_CURRENCIES_ISO_CODES
      ? dynamicParameters.EXCHANGE_CURRENCIES_ISO_CODES.filter(
        currencyCode => currencyCode !== bookingCurrencyCode && !!getCurrency(currencyCode)
      ).map(currencyCode => ({ value: currencyCode, label: getCurrency(currencyCode).name }))
      : [];
  }, [dynamicParameters, props.bookingCurrency]);

  // New options (flag ON) — from available-currencies endpoint
  const { sortedCurrencies } = useAvailableCurrencies(isPaymentCurrencyChangeEnabled);

  const newCurrencyOptions: IMultiselectValueLabelPair[] = useMemo(() => {
    const bookingCurrencyCode = getCurrencyBySymbol(props.bookingCurrency ?? '').code;
    return sortedCurrencies
      .filter(c => c.to.code !== bookingCurrencyCode)
      .map(c => ({
        value: c.to.code,
        label: `${c.to.code} - ${c.to.name}`,
        iconHtml: <CurrencyFlag currencyCode={c.to.code} className="mr-[8px]" />,
        iconAlign: 'left' as const,
      }));
  }, [sortedCurrencies, props.bookingCurrency]);

  const renderTooltipContentElement = useCallback(
    () => (
      <div className="inv-tooltip text-xs leading-14px p-5px font-pt-sans border border-gray-10 text-left">
        Refresh exchange rate to issue new invoice with updated information
      </div>
    ),
    []
  );
  const currentlyEditingProductData = useSelector(BreakdownSelectors.currentlyEditingLineItemSelector);
  const currentlyEditingProductIndex = useSelector(BreakdownSelectors.currentlyEditingLineItemIndexSelector);
  const exchangeCurrencyCode = useSelector(BreakdownSelectors.exchangeCurrencyCodeSelector);
  const exchangeCurrencyRate = useSelector(BreakdownSelectors.exchangeCurrencyRateSelector);
  const exchangeCurrencyRequest = useSelector(BreakdownSelectors.exchangeCurrencyRequestSelector);
  const exchangeCurrencyTimeStamp = useSelector(BreakdownSelectors.exchangeCurrencyTimeStampSelector);
  const isInternalUser = useSelector(AuthSelectors.isInternalUser);
  const isFinanceUser = useSelector(AuthSelectors.isFinanceUser);
  const proformaInvoice = useSelector(automaticProformaInvoiceToTaSelector);
  const isBreakdownCurrencyExchangeModified = useSelector(
    BreakdownSelectors.isBreakdownCurrencyExchangeModifiedSelector
  );
  const showCurrencyRateError = exchangeCurrencyRequest === ENetworkRequestStatus.ERROR;
  const hasExchangeOptions = isPaymentCurrencyChangeEnabled ? newCurrencyOptions.length > 0 : availableCurrenciesToExchange.length > 0;
  const showCurrencyExchange = (isInternalUser || isFinanceUser) && hasExchangeOptions;

  if (roomGroups.length <= 0) {
    return (
      <div className="headline-breakdown mb-8">
        <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
          <div className="border border-solid border-gray-40 py-6 px-5 my-5">
            <span className="block font-pt-sans text-17px leading-22px font-bold">Accommodation</span>
            <span className="block font-pt-sans text-15px leading-20px">No accommodation added</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="headline-breakdown mb-8">
      <AddActivityModalContainer bookingUuid={props.bookingUuid} bookingCurrencySymbol={props.bookingCurrency} />
      <AddTransferModalContainer bookingUuid={props.bookingUuid} bookingCurrencySymbol={props.bookingCurrency} />
      <AddGroundServiceModalContainer bookingUuid={props.bookingUuid} bookingCurrencySymbol={props.bookingCurrency} />
      <AddAccommodationModal bookingUuid={props.bookingUuid} bookingCurrencySymbol={props.bookingCurrency} />
      <EditAccommodationModal bookingUuid={props.bookingUuid} bookingCurrency={props.bookingCurrency || ''} />

      {/* if editing, load the modal */}
      {!isNil(currentlyEditingProductData) && !isNil(topNavigationData) && !isNil(props.bookingCurrency) && (
        <EditAncillaryProductModalContainer
          currencySymbol={props.bookingCurrency}
          bookingUuid={props.bookingUuid}
          bookingCurrencySymbol={props.bookingCurrency}
          headlineLineItemBreakdown={props.headlineLineItemBreakdown}
          currentlyEditingLineItem={currentlyEditingProductData}
          currentlyEditingLineItemIndex={currentlyEditingProductIndex || 0}
          arrivalDate={topNavigationData.arrivalDate || new Date().toISOString()}
          departureDate={topNavigationData.departureDate || new Date().toISOString()}
        />
      )}

      <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
        {roomGroups.map(
          (roomGroup: { hotelName: string; hotelUuid: string; rooms: IHeadlineLineItemBreakdownAccommodationLineItem[] }) => {
            const rooms = roomGroup.rooms.map((accom, index) => {
              const isLiveRate =
                !!accom.externalBooking ||
                accom.roomRateSource === ERateSource.SUPPLIER_UNCONFIRMED ||
                accom.roomRateSource === ERateSource.SUPPLIER_CONFIRMED;

              return (
                <div key={accom.uuid} className="contents">
                  {index === 0 && (
                    <>
                      <p className="m-0 font-noe-display font-normal text-black text-[21px] leading-[27px]">
                        {roomGroup.hotelName}
                      </p>
                      <div>
                        <AnnouncementsDisplay
                          hotelUuid={roomGroup.hotelUuid}
                          checkInDate={topNavigationData?.arrivalDate || null}
                          checkOutDate={topNavigationData?.departureDate || null}
                          appearIn="BREAKDOWN"
                          enabled={true}
                          className='mt-0'
                        />
                      </div>
                    </>
                  )}
                  <div className="flex items-center relative">
                    <AccommodationBlock
                      key={`Accommodation-${accom.uuid}`}
                      index={index}
                      accom={accom}
                      setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
                      removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
                      addHeadlineLineItemAction={props.addHeadlineLineItemAction}
                      bookingCurrencySymbol={props.bookingCurrency || ''}
                      setBreakdownHoldAction={props.setBreakdownHold}
                      bookingStatus={props.bookingStatus}
                      isEditable={props.isEditable}
                      isPurchaseCostEditable={props.isPurchaseCostEditable}
                      isSR={props.isSR}
                      isTA={props.isTA}
                      isAdmin={props.isAdmin}
                      isFinanceUser={props.isFinanceUser}
                      isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
                      isSales={props.isSales}
                      isLiveRate={isLiveRate}
                      isLatestVersion={props.isLatestVersion}
                      allAccommodationItems={Object.values(headlineLineItemBreakdown.Accommodation.items)}
                      bookingUuid={props.bookingUuid}
                      bookingSearchItem={props.bookingSearchItem}
                      isOldVersion={isOldVersion}
                      allocationGuests={accommodationsAllocations[accom.uuid] || []}
                      isGuestAllocationEnabled={dynamicParameters.ENABLE_GUEST_ALLOCATION_TEMP || false}
                    />
                  </div>
                </div>
              );
            });

            return rooms;
          }
        )}

        {(props.isSR || props.isAdmin) && props.isEditable && (
          <FluidButton className="add-accommodation-button" type="primary" onClick={handleAddAccommodationButton}>
            Add Accommodation
          </FluidButton>
        )}
      </div>

      <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
        <p className="m-0 font-noe-display font-normal text-black text-[21px] leading-[27px]">Transfers</p>
        <GenericProductsBlock
          suppliers={transfersSuppliers}
          productType="Transfer"
          productDisplayType="Transfer"
          className="mt-4"
          lineItems={headlineLineItemBreakdown.Transfer.items}
          includeTableHeaders={true}
          setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
          removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
          subTotalCents={headlineLineItemBreakdown.Transfer.subtotalCents}
          bookingCurrencySymbol={props.bookingCurrency}
          isPurchaseCostEditable={props.isPurchaseCostEditable}
          isSR={props.isSR}
          isFinanceUser={props.isFinanceUser}
          isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
          isAdmin={props.isAdmin}
          cta={
            props.isEditable ? (
              <button
                className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                onClick={() => {
                  // OWA-7180
                  if (dynamicParameters.ENABLE_ADD_TRANSFERS_MODAL) {
                    handleAddAncillaryTransfer();
                  } else {
                    props.addHeadlineLineItemAction('Transfer.items');
                  }
                }}
              >
                Add Transfer
              </button>
            ) : (
              undefined
            )
          }
          isEditable={props.isEditable}
          allocationItems={getAllocationItems(
            headlineLineItemBreakdown.Transfer.items,
            allocationGuests,
            allocations,
            true
          )}
          isGrouped={true}
          purchaseCostSubtotalCents={headlineLineItemBreakdown.Transfer.purchaseCostSubtotalCents}
          isTa={props.isTA}
        />
      </div>

      <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
        <p className="m-0 font-noe-display font-normal text-black text-[21px] leading-[27px]">Ground Services</p>
        <GenericProductsBlock
          suppliers={groundServicesSuppliers}
          productType={'Ground Service'}
          productDisplayType={'Ground Service'}
          className="mt-4"
          includeTableHeaders={true}
          lineItems={headlineLineItemBreakdown['Ground Service'].items}
          setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
          removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
          subTotalCents={headlineLineItemBreakdown['Ground Service'].subtotalCents}
          bookingCurrencySymbol={props.bookingCurrency}
          isEditable={props.isEditable}
          isPurchaseCostEditable={props.isPurchaseCostEditable}
          isSR={props.isSR}
          isFinanceUser={props.isFinanceUser}
          isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
          isAdmin={props.isAdmin}
          cta={
            props.isEditable ? (
              <button
                className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                onClick={() => {
                  if (dynamicParameters.ENABLE_ADD_GROUND_SERVICE_MODAL) {
                    handleAddAncillaryGroundService();
                  } else {
                    props.addHeadlineLineItemAction('Ground Service.items');
                  }
                }}
              >
                Add Ground Service
              </button>
            ) : (
              undefined
            )
          }
          allocationItems={getAllocationItems(
            headlineLineItemBreakdown['Ground Service'].items,
            allocationGuests,
            allocations,
            true
          )}
          isGrouped={true}
          purchaseCostSubtotalCents={headlineLineItemBreakdown['Ground Service'].purchaseCostSubtotalCents}
          isTa={props.isTA}
        />
      </div>

      <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
        <p className="m-0 font-noe-display font-normal text-black text-[21px] leading-[27px]">Activities</p>
        <GenericProductsBlock
          suppliers={activitiesSuppliers}
          productType="Bespoke"
          productDisplayType="Activity"
          className="mt-4"
          includeTableHeaders={true}
          lineItems={headlineLineItemBreakdown.Bespoke.items}
          setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
          removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
          subTotalCents={headlineLineItemBreakdown.Bespoke.subtotalCents}
          bookingCurrencySymbol={props.bookingCurrency}
          isEditable={props.isEditable}
          isPurchaseCostEditable={props.isPurchaseCostEditable}
          isSR={props.isSR}
          isFinanceUser={props.isFinanceUser}
          isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
          isAdmin={props.isAdmin}
          allowNegative
          cta={
            props.isEditable ? (
              <button
                className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                onClick={() => {
                  // OWA-6783
                  if (dynamicParameters.ENABLE_ADD_ACTIVITIES_MODAL) {
                    handleAddAncillaryActivity();
                  } else {
                    props.addHeadlineLineItemAction('Bespoke.items');
                  }
                }}
              >
                Add Activity
              </button>
            ) : (
              undefined
            )
          }
          allocationItems={getAllocationItems(
            headlineLineItemBreakdown.Bespoke.items,
            allocationGuests,
            allocations,
            false
          )}
          isGrouped={false}
          purchaseCostSubtotalCents={headlineLineItemBreakdown.Bespoke.purchaseCostSubtotalCents}
          isTa={props.isTA}
        />
      </div>

      <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
        <AdditionalChargesBlock
          titleJSX={<p className="m-0 font-noe-display font-normal text-black text-[21px] leading-[27px]">{isOldVersion ? 'Supplement' : 'Additional Charges'}</p>}
          productType={'Supplement'}
          className="mt-4"
          includeTableHeaders={false}
          lineItems={headlineLineItemBreakdown.Supplement.items}
          setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
          removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
          subTotalCents={headlineLineItemBreakdown.Supplement.subtotalCents}
          bookingCurrencySymbol={props.bookingCurrency}
          isEditable={props.isEditable}
          isPurchaseCostEditable={props.isPurchaseCostEditable}
          isTa={props.isTA}
          isSR={props.isSR}
          isFinanceUser={props.isFinanceUser}
          isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
          isAdmin={props.isAdmin}
          allowNegative
          cta={
            props.isEditable ? (
              <button
                className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                onClick={() => {
                  props.addHeadlineLineItemAction('Supplement.items');
                }}
              >
                Add
              </button>
            ) : (
              undefined
            )
          }
        />

        <SubtotalsRow
          isTa={props.isTA}
          subTotalPurchaseCents={headlineLineItemBreakdown.Supplement.purchaseCostSubtotalCents || 0}
          subTotalSalesCents={headlineLineItemBreakdown.Supplement.subtotalCents || 0}
          bookingCurrencySymbol={props.bookingCurrency}
        />
      </div>

      {/*Use the old block in case of old version*/}
      {isOldVersion && (
        <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
          <ProductsBlock
            titleJSX={<h2 className="text-17px text-left m-0">Additional Hotel Charges</h2>}
            productType={'Fine'}
            className="mt-4"
            includeTableHeaders={false}
            lineItems={headlineLineItemBreakdown.Fine.items}
            setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
            removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
            subTotalCents={headlineLineItemBreakdown.Fine.subtotalCents}
            bookingCurrencySymbol={props.bookingCurrency}
            isEditable={props.isEditable}
            isPurchaseCostEditable={props.isPurchaseCostEditable}
            isTa={props.isTA}
            isSR={props.isSR}
            isFinanceUser={props.isFinanceUser}
            isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
            isAdmin={props.isAdmin}
            isSales={props.isSales}
            allowNegative
            cta={
              props.isEditable ? (
                <button
                  className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                  onClick={() => {
                    props.addHeadlineLineItemAction('Fine.items');
                  }}
                >
                  Add Hotel Charge
                </button>
              ) : (
                undefined
              )
            }
          />
        </div>
      )}

      {(props.isSR || props.isAdmin || props.isFinanceUser) && (
        <div className="mt-4 pt-4 pb-4 pl-4 pr-4 border border-solid border-gray-40">
          <FinanceAdjustmentProductsBlock
            titleJSX={<h2 className="text-17px text-left m-0">Finance Adjustment</h2>}
            productType={'Finance Adjustment'}
            className="mt-4"
            includeTableHeaders={false}
            lineItems={headlineLineItemBreakdown['Finance Adjustment']?.items ?? []}
            setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
            removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
            subTotalCents={headlineLineItemBreakdown.Bespoke.subtotalCents}
            bookingCurrencySymbol={props.bookingCurrency}
            isEditable={props.isEditable}
            isPurchaseCostEditable={props.isPurchaseCostEditable}
            isTa={props.isTA}
            isSR={props.isSR}
            isFinanceUser={props.isFinanceUser}
            isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
            isAdmin={props.isAdmin}
            isSales={props.isSales}
            allowNegative
            cta={
              props.isEditable ? (
                <button
                  className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                  onClick={() => {
                    props.addHeadlineLineItemAction('Bespoke.items');
                  }}
                >
                  Add Activity
                </button>
              ) : (
                undefined
              )
            }
          />

          {(headlineLineItemBreakdown['Finance Adjustment']?.items ?? []).length <= 0 && (
            <span className="text-14px text-gray-80">This booking has no finance adjustment rows</span>
          )}
        </div>
      )}

      <div className="mt-4 mb-4 bg-teal-20 p-4">
        <table className="w-full">
          <thead>
            <tr>
              <th className="margin uppercase text-xs text-left text-gray-80">Margin</th>
              <th className="cost-to-client uppercase text-xs text-right text-gray-80">Total Cost To Client</th>

              {shouldShowPurchaseCost && (
                <th className="purchase-cost uppercase text-xs flex items-center justify-end text-gray-80">
                  {!!props.purchaseCostReviewStatus && (
                    <PurchaseCostReviewStatus className="text-15px mr-2" status={props.purchaseCostReviewStatus} />
                  )}
                  Purchase Cost
                </th>
              )}

              <th className="cost-to-ta uppercase text-xs text-right text-gray-80 pr-10">Cost To Tp</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="margin flex flex-row">
                  <DecimalInput
                    disabled={!props.isMarginEditable}
                    value={props.headlineLineItemBreakdown.MarginPercentage || 0}
                    className={classnames('border text-base border-solid border-gray-40 p-2 w-[90px] font-pt-sans', {
                      'bg-gray-10': props.isEditable === false,
                    })}
                    decimalPlaces={5}
                    onBlur={updatedValue => {
                      props.setHeadlineLineItemBreakdownMarginPercentage(updatedValue as number);
                    }}
                  />
                  <span className="block p-2 ml-1 text-xl">%</span>
                  <MarginDisplaySpan className="info">{`Your current margin will be ${props.bookingCurrency
                    }${formatPrice(headlineLineItemBreakdown.MarginCalculatedValueCents! / 100)}`}</MarginDisplaySpan>
                </div>
              </td>
              <td className="text-right w-1/6">
                {headlineLineItemBreakdown.TotalCostToClientCents && (
                  <span className="cost-to-client text-xl font-bold uppercase text-right">
                    {props.bookingCurrency}
                    {formatPrice(headlineLineItemBreakdown.TotalCostToClientCents / 100)}
                  </span>
                )}
              </td>
              {(props.isSR || props.isFinanceUser) && (
                <td className="text-right w-1/6">
                  <span className="cost-before-discount text-lg font-bold uppercase text-right">
                    {props.bookingCurrency}
                    {formatPrice((headlineLineItemBreakdown.PurchaseCostSubtotalCents || 0) / 100)}
                  </span>
                </td>
              )}
              <td className="text-right w-1/6 pr-10">
                {headlineLineItemBreakdown.SubtotalCents && (
                  <span className="cost-to-ta text-lg font-bold uppercase text-right">
                    {props.bookingCurrency}
                    {formatPrice(headlineLineItemBreakdown.SubtotalCents / 100)}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
        {/* Exchange Currency */}
        {showCurrencyExchange && (
          <div className="pr-10">
            <VerticalSpace height="12px" />
            <div className="h-[0px] border border-gray-40 border-dashed" />
            <VerticalSpace height="12px" />
            <div className="flex justify-between items-end">
              <div className="flex items-end gap-[10px]">
                <div className="flex-1 max-w-250px">
                  <div className="margin uppercase text-xs text-left text-gray-80 mb-[5px]">CURRENCY EXCHANGE</div>
                  {isPaymentCurrencyChangeEnabled ? (
                    <Multiselect
                      className="bg-ivory w-[220px]"
                      itemsClassname="bg-ivory !w-[280px]"
                      itemCtaClassName="hover:bg-gray-10"
                      options={newCurrencyOptions}
                      isCloseOnSelect
                      hideCheckboxes
                      isSingleSelectMode
                      isIncludeClearButton={props.isLatestVersion}
                      disabled={!props.isLatestVersion}
                      onUpdate={handleSelectExchangeCurrency}
                      selectedValues={exchangeCurrencyCode ? [exchangeCurrencyCode as string] : []}
                      placeholderText="Currency"
                      showSelectedIcon
                    />
                  ) : (
                    <Multiselect
                      className="bg-ivory w-[220px]"
                      itemsClassname="bg-ivory"
                      itemCtaClassName="hover:bg-gray-10"
                      options={availableCurrenciesToExchange}
                      isCloseOnSelect={true}
                      hideCheckboxes={true}
                      isSingleSelectMode={true}
                      isIncludeClearButton={props.isLatestVersion}
                      disabled={!props.isLatestVersion}
                      onUpdate={handleSelectExchangeCurrency}
                      selectedValues={exchangeCurrencyCode ? [exchangeCurrencyCode as string] : []}
                      placeholderText="Select ..."
                    />
                  )}
                </div>
                {exchangeCurrencyRate && exchangeCurrencyTimeStamp && (
                  <div className="my-[9px] text-black text-[13px] font-normal font-pt-sans">
                    1 {getCurrencyBySymbol(props.bookingCurrency ?? '').code} = {exchangeCurrencyRate}{' '}
                    {exchangeCurrencyCode}{' '}
                    {!isBreakdownCurrencyExchangeModified &&
                      proformaInvoice &&
                      `(INVOICE: ${formatDate(new Date(proformaInvoice.date), 'dd MMM yy')})`}
                  </div>
                )}
                {exchangeCurrencyRate && (
                  <HidingTooltip position="bottom-right" renderTooltipContent={renderTooltipContentElement}>
                    <RefreshComponent onClick={handleRefreshCurrencyChange} className="cursor-pointer mb-[7px]" />
                  </HidingTooltip>
                )}
                {showCurrencyRateError && (
                  <p className="my-[9px] font-pt-sans text-red-95 text-15px leading-19px  m-0 italic">
                    Something went wrong.
                  </p>
                )}
              </div>
              <div className="currency-exchange-value my-[9px]">
                <span className="cost-to-ta text-lg font-bold uppercase text-right">
                  {exchangeCurrencyRate && headlineLineItemBreakdown.SubtotalCents && exchangeCurrencyCode ? (
                    `${getCurrencySymbol(exchangeCurrencyCode)}${formatPrice(
                      (headlineLineItemBreakdown.SubtotalCents / 100) * exchangeCurrencyRate
                    )}`
                  ) : (
                    <span className="tracking-[2px]">---</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
